import { z } from 'zod';
/**
 * S53-5emp: the CLOSED registry of release flags (ADR 0044). Definitions are code; only VALUES are rows
 * (CLAUDE.md "Hard-won lessons"). Order is display order in Control Tower. snake_case, ≤ 64 chars.
 */
export const RELEASE_FLAG_KEYS = [
    'company_sign_in',
    'social_sign_in',
    'directory_sync',
    'slack_notifications',
    'teams_notifications',
    'release_canary',
];
export const releaseFlagKeySchema = z.enum(RELEASE_FLAG_KEYS);
export const RELEASE_FLAG_KEY_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
export const RELEASE_FLAG_KEY_MAX_LENGTH = 64;
const RELEASE_FLAG_KEY_SET = new Set(RELEASE_FLAG_KEYS);
export function isReleaseFlagKey(value) {
    return RELEASE_FLAG_KEY_SET.has(value);
}
/**
 * Developer-facing (NOT user-facing; the Control Tower label/description are i18n keys under
 * `platform:controlTower.releaseFlags.flags.<key>`). A Record, so a key without a description does not compile.
 */
export const RELEASE_FLAG_DESCRIPTIONS = {
    company_sign_in: 'Company sign-in: SAML/OIDC connections, domain verification and enforcement (S50-kqp9). Gated in S53-5emp Phase 3.',
    social_sign_in: 'Social sign-in with Google, Microsoft and Apple (S50-kqp9). Environment default only before the tenant is known. Gated in S53-5emp Phase 4.',
    directory_sync: 'External directory sync: Google, Entra and SCIM provisioning (S49-3u50). Gated in S53-5emp Phase 2.',
    slack_notifications: 'Slack host DMs and channel copies (PRD-010). Gated in S53-5emp Phase 1b.',
    teams_notifications: 'Microsoft Teams host DMs and channel copies (PRD-010). Gated in S53-5emp Phase 1b.',
    release_canary: 'Gates nothing. Exercises the release-flag pipeline (storage, audit, Control Tower, resolution) in tests, e2e and after a deploy without hiding a real feature.',
};
/** override ?? default ?? fallback ?? false, per key. Pure. Returns a fresh object with exactly RELEASE_FLAG_KEYS. */
export function resolveReleaseFlagValues(input) {
    const out = {};
    for (const key of RELEASE_FLAG_KEYS) {
        out[key] = input.overrides[key] ?? input.defaults[key] ?? input.fallback ?? false;
    }
    return out;
}
/** default ?? fallback ?? false — what a tenant with no override gets. Pure. */
export function effectiveReleaseFlagDefault(defaultEnabled, fallbackEnabled) {
    return defaultEnabled ?? fallbackEnabled ?? false;
}
/** `{ company_sign_in: z.boolean(), … }` built from RELEASE_FLAG_KEYS (cast to the mapped type; no `any`). */
export const releaseFlagValuesSchema = z.object(Object.fromEntries(RELEASE_FLAG_KEYS.map((key) => [key, z.boolean()]))).strict();
/** `GET /api/web/auth/release-flags` payload (route lands in Phase 1b). */
export const resolvedReleaseFlagsDTOSchema = z.object({ flags: releaseFlagValuesSchema }).strict();
/** Under `/api/web`. Under `/auth/` on purpose: the plan-selection wall allowlists it (require-plan-selection.ts:38). */
export const RELEASE_FLAGS_PATH = '/auth/release-flags';
/** Under `/api/web${CONTROL_TOWER_API_PATH}`. */
export const CONTROL_TOWER_RELEASE_FLAGS_PATH = '/release-flags';
/** Body of every PUT that sets a value. */
export const releaseFlagValueUpdateSchema = z.object({ enabled: z.boolean() }).strict();
export const releaseFlagKeyParamsSchema = z.object({ key: releaseFlagKeySchema }).strict();
export const releaseFlagOverrideParamsSchema = z
    .object({ key: releaseFlagKeySchema, tenantId: z.string().min(1).max(64) })
    .strict();
export const controlTowerReleaseFlagOverrideDTOSchema = z
    .object({
    tenantId: z.string().min(1),
    tenantName: z.string().min(1),
    tenantSlug: z.string().min(1),
    enabled: z.boolean(),
    updatedAt: z.string().datetime(),
    updatedByUserId: z.string().min(1).nullable(),
})
    .strict();
export const controlTowerReleaseFlagDTOSchema = z
    .object({
    key: releaseFlagKeySchema,
    /** null = no default row in this environment. */
    defaultEnabled: z.boolean().nullable(),
    defaultUpdatedAt: z.string().datetime().nullable(),
    /** effectiveReleaseFlagDefault(defaultEnabled, fallbackEnabled). */
    effectiveDefault: z.boolean(),
    /** Ordered by tenant name, ascending. */
    overrides: z.array(controlTowerReleaseFlagOverrideDTOSchema),
})
    .strict();
/**
 * `GET /api/web/control-tower/release-flags` and the answer to every write. A single bounded resource (one entry
 * per registry key), not a paged collection — hence `{ data }`, not `{ data, meta }`.
 */
export const controlTowerReleaseFlagsDTOSchema = z
    .object({
    /** null = no fallback row: every flag without a default resolves off. */
    fallbackEnabled: z.boolean().nullable(),
    fallbackUpdatedAt: z.string().datetime().nullable(),
    /** Exactly one entry per RELEASE_FLAG_KEYS member, in registry order. */
    flags: z.array(controlTowerReleaseFlagDTOSchema),
})
    .strict();
//# sourceMappingURL=release-flags.js.map